import { assignRoles } from "../team/rosterComposition.baseball.ts";
export type PositionProfile = { pid?:number; age:number; ovrs:Record<string,number>; pots:Record<string,number> };
export const clamp=(x:number,a=0,b=1)=>Math.max(a,Math.min(b,x));
/** Canonical summation order ensures bit-identical results for shuffled inputs. */
export const portfolio=(values:number[], exponent:number)=> {
 const positive=values.filter(x=>x>0).sort((a,b)=>a-b);
 const negative=values.filter(x=>x<0).sort((a,b)=>a-b).reduce((s,x)=>s+x,0);
 return Math.pow(positive.reduce((s,x)=>s+x**exponent,0),1/exponent)+negative;
};
export const packageWeights=(values:number[], exponent=1.10)=> {
 const sum=values.filter(x=>x>0).sort((a,b)=>a-b).reduce((s,x)=>s+x**exponent,0);
 const total=Math.pow(sum,1/exponent);
 return values.map(v=>v>0 && sum>0 ? total*v**(exponent-1)/sum : 1);
};
/** Minor leaguers close deals: the first three a team receives that are worth less than that are each worth a small
 * share of the biggest piece in the trade (5%, 3.5% and 2.5%), so together they make up a gap of about 11%. Being a
 * share of the biggest piece, they can never be the deal themselves. Prospects worth more keep their value and take no
 * slot; any more fillers only count their own value. Returns the floored values and which ones are fillers. */
const FILLER_SHARES=[.05,.035,.025];
export const fillerFloors=(values:number[],biggest:number)=>{
 const out=[...values];
 const fillers=new Set<number>();
 const top=FILLER_SHARES[0]!*Math.max(0,biggest);
 const order=values.map((_,i)=>i).filter(i=>values[i]!<top).sort((a,b)=>values[b]!-values[a]!||a-b);
 order.slice(0,FILLER_SHARES.length).forEach((i,k)=>{out[i]=Math.max(values[i]!,FILLER_SHARES[k]!*Math.max(0,biggest));fillers.add(i);});
 return {values:out,fillers};
};
export const immediateImpact=(C:number,delta:number)=>(.20+1.80*clamp(C)**1.5)*Math.tanh(delta/4);
/** Max ratio between the AI's valuation of its own pick (when dealing with the user) and the pick's universal value.
 * 1.5 normally, rising to 2.0 at the top of the round, where the value curve is steepest and easiest to manipulate. */
export const pickPremiumCap=(roundPosition:number)=>1.5+.5*clamp((6-roundPosition)/5);
/** Contenders value the future part of a young incoming player less (C^1.5 weighted, fading out by age 24).
 * Floored at 0.6 so elite prospects keep most of their economic value; incoming side only, so a contender never
 * sells its own prospects cheaper and veterans' prices don't change. */
export const contenderProspectFactor=(C:number,age:number)=>Math.max(.6,1-.5*clamp(C)**1.5*clamp((24-age)/6));
export const competitiveIndex=(strategyScore:number,strength:number,record:number)=>clamp(.55*strategyScore+.30*strength+.15*record);
export const coreScore=(age:number,pot:number,rank:number,years:number)=>
 clamp((26-age)/6)*clamp((pot-50)/30)*clamp((6-rank)/3)*clamp(years/4);
export const holdMultiplier=(C:number,core:number)=>1+.12*(1-clamp(C))*clamp(core);
const groups:Record<string,string[]>={C:['C'],middleIF:['2B','SS'],cornerIF:['1B','3B'],OF:['LF','CF','RF'],SP:['SP'],RP:['RP']};
export const defensiveGroups=(p:PositionProfile)=> {
 const scores=Object.entries(groups).map(([group,positions])=>({group,score:Math.max(...positions.map(pos=>Math.max(p.ovrs[pos]??0,p.age<=25?p.pots[pos]??0:0)))}));
 const best=Math.max(...scores.map(x=>x.score));
 const usable=scores.filter(x=>x.score>=Math.max(35,best-8));
 const sum=usable.reduce((s,x)=>s+x.score,0);
 return usable.map(x=>({group:x.group,share:x.score/Math.max(1,sum)}));
};
/** Concentration alone isn't redundancy: require surplus over the organization's role needs. */
export const redundancy=(players:{value:number;profile:PositionProfile;fitFactor:number}[])=> {
 const totals:Record<string,number>={};let total=0;
 for(const p of [...players].sort((a,b)=>(a.profile.pid??0)-(b.profile.pid??0))) {
  if(p.value<=0)continue;total+=p.value;
  for(const {group,share} of defensiveGroups(p.profile)) totals[group]=(totals[group]??0)+p.value*share;
 }
 const concentration=total>0?Math.max(0,...Object.values(totals))/total:0;
 const blocked=players.length>1?players.filter(x=>x.fitFactor<1).reduce((s,x)=>s+Math.max(0,x.value),0)/Math.max(1e-9,total):0;
 return {concentration,penalty:.10*clamp((concentration-.55)/.45)*blocked};
};
const positions=['C','1B','2B','3B','SS','LF','CF','RF',...Array(5).fill('SP'),...Array(7).fill('RP')];
/** One assignment for all incoming players and incumbents, separately at each ETA.
 * Young prospects retain defensive roles; DH is available only to MLB-age players. */
export const jointFits=(incoming:PositionProfile[],roster:PositionProfile[])=> {
 const result=new Map<number,{factor:number;reason:string}>();
 for(const eta of new Set(incoming.map(p=>Math.max(0,19-p.age)))) {
  const pool=[...roster,...incoming].sort((a,b)=>(a.pid??0)-(b.pid??0));
  const slots=[...positions,'DH'];
  const rating=(p:PositionProfile,pos:string)=> {
   if(p.age+eta<19 || (pos==='DH'&&p.age<20))return 0;
   const now=p.ovrs[pos]??0;
   return eta>0 ? Math.max(0,now+clamp(eta/4)*(Math.max(now,p.pots[pos]??now)-now)-Math.max(0,p.age+eta-30)*2):now;
  };
  const scores=slots.map(pos=>pool.map(p=>rating(p,pos)));
  const assigned=assignRoles(scores);
  for(const p of incoming.filter(p=>Math.max(0,19-p.age)===eta)) {
   const index=pool.indexOf(p);const candidateRole=assigned.indexOf(index);const role=candidateRole>=0&&scores[candidateRole]![index]!>0?candidateRole:-1;
   const candidates=slots.filter(pos=>pos!=='DH'||p.age>=20);
   const pos=role>=0?slots[role]!:candidates.sort((a,b)=>rating(p,b)-rating(p,a)||a.localeCompare(b))[0]!;
   const count=slots.filter(x=>x===pos).length;
   const baseline=roster.map(x=>rating(x,pos)).sort((a,b)=>b-a)[count-1]??0;
   const gain=rating(p,pos)-baseline;
   // Filling a hole matters: +2.5% per point above the starter he replaces, up to +25% at 10 points (it was +0.4%,
   // capped at +8%, so two starters going straight into a weak rotation barely counted). Below him, a small discount.
   const factor=role>=0?(gain>0?clamp(1+gain*.025,1,1.25):clamp(1+gain*.004,.94,1)):.96;
   result.set(p.pid??index,{factor,reason:`${eta>0?`Future ${pos} / ETA ${eta}y`:role>=0?`Current ${pos}`:`Depth at ${pos}`} (${role>=0?'assigned role':'competing for role'})`});
  }
 }
 return result;
};
export const organizationalFit=(p:PositionProfile,roster:PositionProfile[])=>jointFits([p],roster).get(p.pid??[...roster,p].sort((a,b)=>(a.pid??0)-(b.pid??0)).indexOf(p))??{factor:1,reason:'Organizational depth'};
